import React, { FC, useEffect, useState } from 'react'
import { FiPackage, FiRefreshCw, FiSend, FiXCircle } from 'react-icons/fi'
import { toast } from 'react-toastify'
import { DateRangePickerComponent } from '@syncfusion/ej2-react-calendars/src/daterangepicker/daterangepicker.component'
import CN from 'classnames'
import {
  useGetAgeingDataCount,
  useGetDispatchBarChartData,
  useGetDispatchKPIDataCount,
  useGetPartnerOrderTypes,
  useGetPartnerTable,
} from 'framework/api/methods'
import useGetFulfillmentDataCount from 'framework/api/methods/dashboard/useGetFulfillmentDataCount'
import { TRUE } from 'sass'

import { MultiSelect } from 'components/atoms/MultiSelect'

import { CustomerSelector } from './CustomerSelector'
import { DispatchBarChart } from './DispatchBarChart'
import { DispatchPieChart } from './DispatchPieChart'
import { OrderAgeingCard } from './OrderAgeingCard'
import { OrderStatusCard } from './OrderStatusCard'
import OrderTotalCard from './OrderTotalCard'

export interface MWDashboardProps {
  [x: string]: any
}

const customizeClass = CN(
  'w-[350px] px-6 py-4 text-lg font-semibold text-white rounded-lg hover:bg-blue-700 flex items-start justify-start',
)
const MWDashboard: FC<MWDashboardProps> = ({ className }: MWDashboardProps) => {
  interface OrderType {
    value: string
    label: string
    disabled: boolean
  }

  interface Partner {
    value: string
    label: string
    disabled: boolean
  }

  const [openOrders, setOpenOrderCount] = useState<any>()

  const [orderStatusSummary, setOrderStatusSummary] = useState<any>([
    { label: 'Confirmed', value: '' },
    { label: 'On hold', value: '' },
    { label: 'Pick & Pack', value: '' },
    { label: 'To be Cons.', value: '' },
  ])
  const [dateError, setDateError] = useState('')
  const [isDateError, setIsDateError] = useState(false)

  const [ageRanges, setAgeRanges] = useState<any>([
    {
      label: '0 to 24 hrs',
      total: '',
      status: [
        { label: 'Confirmed', value: '' },
        { label: 'On hold', value: '' },
        { label: 'Pick & Pack', value: '' },
        { label: 'To be Cons.', value: '' },
      ],
    },
    {
      label: '24 to 72 hrs',
      total: '',
      status: [
        { label: 'Confirmed', value: '' },
        { label: 'On hold', value: '' },
        { label: 'Pick & Pack', value: '' },
        { label: 'To be Cons.', value: '' },
      ],
    },
    {
      label: 'More than 72 hrs',
      total: '',
      status: [
        { label: 'Confirmed', value: '' },
        { label: 'On hold', value: '' },
        { label: 'Pick & Pack', value: '' },
        { label: 'To be Cons.', value: '' },
      ],
    },
  ])

  const [fulfillmentOrderSummary, setFulfillmentOrderSummary] = useState<any>([
    {
      label: 'Open',
      value: '',
      icon: <FiPackage className='text-white' />,
    },
    {
      label: 'Cancelled',
      value: '',
      icon: <FiXCircle className='text-white' />,
    },
    {
      label: 'Dispatched',
      value: '',
      icon: <FiSend className='text-white' />,
    },
  ])

  const [fulfillmentOrderCount, setfulfillmentOrderCount] = useState<any>('')

  const [pieData, setPieData] = useState<any>([
    { name: 'On-time Dispatched', percentage: 0, count: 0 },
    { name: 'Delayed Dispatches', percentage: 0, count: 0 },
  ])

  const [showPieChart, setShowPieChart] = useState(false)
  const [pieError, setPieError] = useState(
    'Select One customer and one order type',
  )

  const [barData, setBarData] = useState<any>([
    { time: '< 24 hours', orders: '' },
    { time: '24–48 hours', orders: '' },
    { time: '48–72 hours', orders: '' },
    { time: '> 72 hours', orders: '' },
  ])

  const [isLoading, setIsLoading] = useState(false)
  const [pieChartIsLoading, setPieChartIsLoading] = useState(false)
  const [barChartIsLoading, setBarChartIsLoading] = useState(false)
  const [isTotalCardLoading, setIsTotalCardLoading] = useState(false)

  const [showBarChart, setShowBarChart] = useState(false)
  const [barChartError, setBarChartError] = useState(
    'Select Customer to view chart',
  )

  const [partnerList, setPartnerList] = useState<Partner[]>([])

  const [selectedPartners, setSelectedPartners] = useState<Partner[]>([])

  const [selectedOrderTypes, setselectedOrderTypes] = useState<OrderType[]>([
    { label: 'All', value: '', disabled: false },
  ])

  const [selectedFulfillmentOrderTypes, setselectedFulfillmentOrderTypes] =
    useState<OrderType[]>([{ label: ' All', value: '', disabled: false }])

  const [partnerOrderTypeList, setOrderTypeList] = useState<any>([])

  const [filterVariables, setFilterVariables] = useState<any>({
    partnerCodes: selectedPartners?.map((partner: Partner) => partner.value),
    orderTypes: selectedOrderTypes?.map((o) => o.value),
    fulfillmentOrderTypes: selectedFulfillmentOrderTypes?.map((o) => o.value),
    fromDate: new Date(
      new Date().getFullYear(),
      new Date().getMonth() - 1,
      1,
      0,
      0,
      0,
      0,
    ).toISOString(),
    toDate: new Date(
      new Date().getFullYear(), // current year
      new Date().getMonth(), // current month …
      0, // … day “0” ⇒ last day of *previous* month
      23,
      59,
      59,
      999,
    ).toISOString(), //,
  })

  function resetAgeingData() {
    setOpenOrderCount('')
    setOrderStatusSummary([
      { label: 'Confirmed', value: '' },
      { label: 'On hold', value: '' },
      { label: 'Pick & Pack', value: '' },
      { label: 'To be Cons.', value: '' },
    ])

    setAgeRanges([
      {
        label: '0 to 24 hrs',
        total: '',
        status: [
          { label: 'Confirmed', value: '' },
          { label: 'On hold', value: '' },
          { label: 'Pick & Pack', value: '' },
          { label: 'To be Cons.', value: '' },
        ],
      },
      {
        label: '24 to 72 hrs',
        total: '',
        status: [
          { label: 'Confirmed', value: '' },
          { label: 'On hold', value: '' },
          { label: 'Pick & Pack', value: '' },
          { label: 'To be Cons.', value: '' },
        ],
      },
      {
        label: 'More than 72 hrs',
        total: '',
        status: [
          { label: 'Confirmed', value: '' },
          { label: 'On hold', value: '' },
          { label: 'Pick & Pack', value: '' },
          { label: 'To be Cons.', value: '' },
        ],
      },
    ])
  }

  function resetFulfillmentData() {
    setPieError('Select One customer and one order type')
    setShowPieChart(false)
    setBarChartError('Select Customer to view chart')
    setShowBarChart(false)
    setFulfillmentOrderSummary([
      {
        label: 'Open',
        value: '',
        icon: <FiPackage className='text-white' />,
      },
      {
        label: 'Cancelled',
        value: '',
        icon: <FiXCircle className='text-white' />,
      },
      {
        label: 'Dispatched',
        value: '',
        icon: <FiSend className='text-white' />,
      },
    ])
    setBarData([
      { time: '< 24 hours', orders: '' },
      { time: '24–48 hours', orders: '' },
      { time: '48–72 hours', orders: '' },
      { time: '> 72 hours', orders: '' },
    ])
    setfulfillmentOrderCount('')
  }

  const handleApplyClick = () => {
    getAgeingOrderDetails()
  }

  const handleRefreshClick = () => {
    getAgeingOrderDetails()
  }

  const handleFulfillmentApplyClick = () => {
    if (filterVariables.fulfillmentOrderTypes.length > 1) {
      setPieError('Select One customer and one order type')
      setShowPieChart(false)
    }
    if (
      filterVariables.partnerCodes.length === 1 &&
      filterVariables.partnerCodes[0] !== 'All' &&
      filterVariables.partnerCodes[0] !== '' &&
      filterVariables.fulfillmentOrderTypes.length === 1
    ) {
      getDispatchKPIDetails()
    }
    getFulfillmentOrderDetails()
    getDispatchBarChartDetails()
  }

  const handleFulfillmentRefreshClick = () => {
    if (filterVariables.fulfillmentOrderTypes.length > 1) {
      setPieError('Select One customer and one order type')
      setShowPieChart(false)
    }
    if (
      filterVariables.partnerCodes.length === 1 &&
      filterVariables.partnerCodes[0] !== 'All' &&
      filterVariables.partnerCodes[0] !== '' &&
      filterVariables.fulfillmentOrderTypes.length === 1
    ) {
      getDispatchKPIDetails()
    }
    getFulfillmentOrderDetails()
    getDispatchBarChartDetails()
  }

  const notify = (message: any, type: any) => {
    if (type === 'error') {
      toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      })
    }
    if (type === 'success') {
      toast.success(message, {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      })
    }
  }

  function FormatDate(dateString: string) {
    // Assuming the input gives you a string like "Thu May 09 2024 00:00:00 GMT 1000 (Australian Eastern Standard Time)"
    // Create a new Date object from the string
    const dateObject = new Date(dateString)
    // Format the date as YYYY-MM-DD
    const year = dateObject.getFullYear()
    const month = dateObject.getMonth() + 1 // getMonth() returns month index (0-11), so add 1 for the actual month
    const day = dateObject.getDate()
    const formattedDate = `${year}/${month.toString().padStart(2, '0')}/${day
      .toString()
      .padStart(2, '0')}`
    return formattedDate
  }

  useEffect(() => {
    if (selectedOrderTypes?.length > 0) {
      const updatedArray = partnerOrderTypeList.map(
        (
          item: { value: string; label: string },
          idx: string | number | any,
        ) => {
          if (
            selectedOrderTypes?.length > 0 &&
            !selectedOrderTypes.some((p) => p.label === 'All')
          ) {
            return {
              ...item,
              disabled: item.label === 'All',
            }
          } else {
            return {
              ...item,
              disabled: item.label !== selectedOrderTypes[idx]?.label,
            }
          }
        },
      )

      setOrderTypeList(updatedArray)
      setFilterVariables((_prev: any) => ({
        ...filterVariables,
        orderTypes: selectedOrderTypes.map((o) => o.label), // Get the correct value from the input
      }))
      if (selectedPartners.length > 0) {
        getAgeingOrderDetails()
      }
    } else {
      const updatedList = partnerOrderTypeList.map(
        (item: { value: string }) => ({
          ...item,
          disabled: false, //  Enable all items
        }),
      )
      resetAgeingData()
      setOrderTypeList(updatedList)
      setFilterVariables((_prev: any) => ({
        ...filterVariables,
        orderTypes: [], // Get the correct value from the input
      }))
    }
  }, [selectedOrderTypes])

  useEffect(() => {
    if (selectedFulfillmentOrderTypes?.length > 0) {
      const updatedArray = partnerOrderTypeList.map(
        (
          item: { value: string; label: string },
          idx: string | number | any,
        ) => {
          if (
            selectedFulfillmentOrderTypes?.length > 0 &&
            !selectedFulfillmentOrderTypes.some((p) => p.label === 'All')
          ) {
            return {
              ...item,
              disabled: item.label === 'All',
            }
          } else {
            return {
              ...item,
              disabled:
                item.label !== selectedFulfillmentOrderTypes[idx]?.label,
            }
          }
        },
      )

      setOrderTypeList(updatedArray)
      setFilterVariables((_prev: any) => ({
        ...filterVariables,
        fulfillmentOrderTypes: selectedFulfillmentOrderTypes.map(
          (o) => o.label,
        ), // Get the correct value from the input
      }))
      if (selectedPartners.length > 0) {
        getFulfillmentOrderDetails()
        if (
          filterVariables.partnerCodes.length === 1 &&
          filterVariables.partnerCodes[0] !== 'All' &&
          filterVariables.partnerCodes[0] !== '' &&
          filterVariables.fulfillmentOrderTypes.length === 1 &&
          selectedFulfillmentOrderTypes.length === 1
        ) {
          getDispatchKPIDetails()
        }
        getDispatchBarChartDetails()
      }
    } else {
      const updatedList = partnerOrderTypeList.map(
        (item: { value: string }) => ({
          ...item,
          disabled: false, //  Enable all items
        }),
      )
      resetFulfillmentData()
      setOrderTypeList(updatedList)
      setFilterVariables((_prev: any) => ({
        ...filterVariables,
        fulfillmentOrderTypes: ['All'], // Get the correct value from the input
      }))
      if (
        (selectedFulfillmentOrderTypes.length === 1 &&
          selectedFulfillmentOrderTypes[0].label === 'All') ||
        selectedFulfillmentOrderTypes.length > 1 ||
        filterVariables.fulfillmentOrderTypes.length > 1
      ) {
        setPieError('Select one customer and one order type')
        setShowPieChart(false)
      }
    }
    if (selectedFulfillmentOrderTypes.length > 1) {
      setPieError('Select one customer and one order type')
      setShowPieChart(false)
    }
  }, [selectedFulfillmentOrderTypes])

  useEffect(() => {
    if (selectedPartners?.length > 0) {
      const updatedArray = partnerList.map(
        (
          item: { value: string; label: string },
          idx: string | number | any,
        ) => {
          if (
            selectedPartners?.length > 0 &&
            !selectedPartners.some((p) => p.label === 'All')
          ) {
            return {
              ...item,
              disabled: item.label === 'All',
            }
          } else {
            return {
              ...item,
              disabled: item.label !== selectedOrderTypes[idx]?.label,
            }
          }
        },
      )
      setPartnerList(updatedArray)
      setFilterVariables((_prev: any) => ({
        ...filterVariables,
        partnerCodes: selectedPartners.map((partner: Partner) => partner.value), // Get the correct value from the input
      }))
      getAgeingOrderDetails()
      getFulfillmentOrderDetails()
      if (
        filterVariables.partnerCodes.length === 1 &&
        filterVariables.partnerCodes[0] !== 'All' &&
        filterVariables.partnerCodes[0] !== '' &&
        filterVariables.fulfillmentOrderTypes.length === 1 &&
        selectedFulfillmentOrderTypes.length === 1
      ) {
        getDispatchKPIDetails()
      }
      getDispatchBarChartDetails()
    } else {
      const updatedPartnerList = partnerList.map((item: Partner) => ({
        ...item,
        disabled: false, //  Enable all items
      }))
      resetAgeingData()
      resetFulfillmentData()
      setPartnerList(updatedPartnerList)
      setFilterVariables((_prev: any) => ({
        ...filterVariables,
        orderTypes: ['All'],
        fulfillmentOrderTypes: ['All'], // Get the correct value from the input
      }))
    }
    if (
      (selectedPartners.length === 1 && selectedPartners[0].label === 'All') ||
      selectedPartners.length > 1 ||
      filterVariables.fulfillmentOrderTypes.length > 1
    ) {
      setPieError('Select one customer and one order type')
      setShowPieChart(false)
      setselectedOrderTypes([{ label: 'All', value: '', disabled: false }])
      setselectedFulfillmentOrderTypes([
        { label: 'All', value: '', disabled: false },
      ])
    }
  }, [selectedPartners])

  /** API Call for get partner list function using react-query */
  const { mutate: getPartnerTableMutate, isLoading: getPartnerTableIsLoading } =
    useGetPartnerTable()
  /** Process to get partner List for integration */
  async function getPartnerList(): Promise<void> {
    getPartnerTableMutate(
      { take: 100, skip: 0, Search: '' },
      {
        onSuccess: ({ data: successData }: any) => {
          const result: any = []
          const allPartnerOption: Partner = {
            label: 'All',
            value: '',
            disabled: false,
          }
          successData.partners.forEach((item: any) => {
            if (item.isActive) {
              result.push({
                label: item.name,
                value: item?.code,
                disabled: false,
              })
            }
          })
          setPartnerList([allPartnerOption, ...result])
        },
        onError: (error: any) => {
          const errData = error?.response?.data || {}
          //notify(errData.message || 'Error', 'error')
        },
      },
    )
  }

  /** API Call For Partner order type List (Advanced Search Modal)  */
  const { mutate: getPartnerOrderTypeListMutate } = useGetPartnerOrderTypes()

  /** Process the get Partner order Type List */
  async function getPartnerOrderTypeList() {
    getPartnerOrderTypeListMutate(filterVariables, {
      onSuccess: ({ data }: any) => {
        const allOption: OrderType = {
            value: '',
            label: 'All',
            disabled: false,
          },
          formattedData = data.orderTypeList.map(
            (item: { label: string; value: string }) => ({
              ...item,
              disabled: true, // Initialize disabled as false
            }),
          )
        const updatedOrderTypeList: OrderType[] = [allOption, ...formattedData]
        setOrderTypeList(updatedOrderTypeList)
      },
      onError: ({ response: { data: errData } }: any) => {
        //notify(errData.message || 'Error...', 'error')
      },
    })
  }

  /** API Call For Order Ageing List (Advanced Search Modal)  */
  const { mutate: getAgeingOrderDetailsMutate } = useGetAgeingDataCount()

  /** Process the get Order Ageing List */
  async function getAgeingOrderDetails() {
    setIsLoading(true)
    getAgeingOrderDetailsMutate(
      {
        partnerCodes: filterVariables.partnerCodes,
        orderTypes: filterVariables.orderTypes,
      },
      {
        onSuccess: ({ data }: any) => {
          const ageingOrderDetails = data.ageingAnalysisOrders
          setOrderStatusSummary([
            {
              label: 'Confirmed',
              value: ageingOrderDetails?.openOrderCount?.confirmedOrders ?? 0,
            },
            {
              label: 'On hold',
              value: ageingOrderDetails?.openOrderCount?.onHoldOrders ?? 0,
            },
            {
              label: 'Pick & Pack',
              value: ageingOrderDetails?.openOrderCount?.pickPackOrders ?? 0,
            },
            {
              label: 'To be Cons.',
              value: ageingOrderDetails?.openOrderCount?.toBeConsigned ?? 0,
            },
          ])
          setOpenOrderCount(
            ageingOrderDetails?.openOrderCount?.totalOrders ?? 0,
          )
          const recentOrders =
            ageingOrderDetails?.timeSeriesOrderCount?.recentOrderCount
          const midAgedOrders =
            ageingOrderDetails?.timeSeriesOrderCount?.midAgedOrderCount
          const agedOrders =
            ageingOrderDetails?.timeSeriesOrderCount?.agedOrderCount
          setAgeRanges([
            {
              label: '0 to 24 hrs',
              total: recentOrders?.totalOrders ?? 0,
              status: [
                {
                  label: 'Confirmed',
                  value: recentOrders?.confirmedOrders ?? 0,
                },
                { label: 'On hold', value: recentOrders?.onHoldOrders ?? 0 },
                {
                  label: 'Pick & Pack',
                  value: recentOrders?.pickPackOrders ?? 0,
                },
                {
                  label: 'To be Cons.',
                  value: recentOrders?.toBeConsigned ?? 0,
                },
              ],
            },
            {
              label: '24 to 72 hrs',
              total: midAgedOrders?.totalOrders ?? 0,
              status: [
                {
                  label: 'Confirmed',
                  value: midAgedOrders?.confirmedOrders ?? 0,
                },
                { label: 'On hold', value: midAgedOrders?.onHoldOrders ?? 0 },
                {
                  label: 'Pick & Pack',
                  value: midAgedOrders?.pickPackOrders ?? 0,
                },
                {
                  label: 'To be Cons.',
                  value: midAgedOrders?.toBeConsigned ?? 0,
                },
              ],
            },
            {
              label: 'More than 72 hrs',
              total: agedOrders?.totalOrders ?? 0,
              status: [
                {
                  label: 'Confirmed',
                  value: agedOrders?.confirmedOrders ?? 0,
                },
                { label: 'On hold', value: agedOrders?.onHoldOrders ?? 0 },
                {
                  label: 'Pick & Pack',
                  value: agedOrders?.pickPackOrders ?? 0,
                },
                { label: 'To be Cons.', value: agedOrders?.toBeConsigned ?? 0 },
              ],
            },
          ])
          setIsLoading(false)
        },
        onError: ({ response: { data: errData } }: any) => {
          setIsLoading(false)
        },
      },
    )
  }

  /** API Call For Order FulFillment List  */
  const { mutate: getFulfillmentOrderDetailsMutate } =
    useGetFulfillmentDataCount()

  /** Process the get Order Fulfillment List */
  async function getFulfillmentOrderDetails() {
    setIsTotalCardLoading(true)
    getFulfillmentOrderDetailsMutate(
      {
        partnerCodes: filterVariables.partnerCodes,
        fulfillmentOrderTypes: filterVariables.fulfillmentOrderTypes,
        fromDate: filterVariables.fromDate,
        toDate: filterVariables.toDate,
      },
      {
        onSuccess: ({ data }: any) => {
          const fulfillmentOrderDetails = data.fulfillmentOrderDetails
          if (fulfillmentOrderDetails?.totalOrders == 0) {
            setShowPieChart(false)
            setPieError('No fulfillment orders found.')
          }
          setFulfillmentOrderSummary([
            {
              label: 'Confirmed',
              value: fulfillmentOrderDetails?.openOrders ?? 0,
              icon: <FiPackage className='text-P-500' />,
            },
            {
              label: 'Cancelled',
              value: fulfillmentOrderDetails?.cancelledOrders ?? 0,
              icon: <FiXCircle className='text-P-500' />,
            },
            {
              label: 'Dispatched',
              value: fulfillmentOrderDetails?.dispatchedOrders ?? 0,
              icon: <FiSend className='text-P-500' />,
            },
          ])
          setfulfillmentOrderCount(fulfillmentOrderDetails?.totalOrders ?? 0)
          setIsLoading(false)
          setIsTotalCardLoading(false)
        },
        onError: ({ response: { data: errData } }: any) => {
          setIsTotalCardLoading(false)
        },
      },
    )
  }

  /** API Call For Order FulFillment List  */
  const { mutate: getDispatchKPIDetailsMutate } = useGetDispatchKPIDataCount()

  /** Process the get Order Fulfillment List */
  async function getDispatchKPIDetails() {
    setPieChartIsLoading(true)
    getDispatchKPIDetailsMutate(
      {
        partnerCodes: filterVariables.partnerCodes,
        fulfillmentOrderTypes: filterVariables.fulfillmentOrderTypes,
        fromDate: filterVariables.fromDate,
        toDate: filterVariables.toDate,
      },
      {
        onSuccess: ({ data }: any) => {
          const dispatchKPIVariance = data.dispatchVarianceCount
          const dispatchKPIRatio = data.dispatchRatio

          setPieData([
            {
              name: 'On-time Dispatched',
              //value: dispatchKPIRatio.onTimeDispatchRatio ?? 0,
              value: dispatchKPIVariance?.onTimeDispatchCount ?? 0,
            },
            {
              name: 'Delayed Dispatches',
              //value: dispatchKPIRatio.delayedDispatchRatio ?? 0,
              value: dispatchKPIVariance?.delayedDispatchedCount ?? 0,
            },
          ])
          setShowPieChart(true)
          setPieError('')
          setPieChartIsLoading(false)
        },
        onError: ({ response: { data: errData } }: any) => {
          //notify(errData.message || 'Error...', 'error')
          setShowPieChart(false)
          setPieChartIsLoading(false)
          if (
            selectedPartners.length === 1 &&
            (selectedPartners[0].label === 'All' ||
              selectedPartners[0].value === '')
          ) {
            setPieError('Select one customer and one order type')
          } else if (
            selectedPartners.length !== 1 ||
            selectedFulfillmentOrderTypes.length !== 1
          ) {
            setPieError('Select one customer and one order type')
          } else {
            setPieError(errData.message || 'Error fetching dispatch KPI data')
          }
        },
      },
    )
  }

  const { mutate: getDispatchBarChartDetailsMutate } =
    useGetDispatchBarChartData()

  /** Process the get Order Fulfillment List */
  async function getDispatchBarChartDetails() {
    setBarChartIsLoading(true)
    getDispatchBarChartDetailsMutate(
      {
        partnerCodes: filterVariables.partnerCodes,
        fulfillmentOrderTypes: filterVariables.fulfillmentOrderTypes,
        fromDate: filterVariables.fromDate,
        toDate: filterVariables.toDate,
      },
      {
        onSuccess: ({ data }: any) => {
          const dispatchBarChartDetails = data.dispatchedBarChartDetails
          if (
            dispatchBarChartDetails?.freshOrdersCount == 0 &&
            dispatchBarChartDetails?.recentOrdersCount == 0 &&
            dispatchBarChartDetails?.staleOrdersCount == 0 &&
            dispatchBarChartDetails?.agedOrdersCount == 0
          ) {
            setShowBarChart(false)
            setBarChartIsLoading(false)
            setBarChartError('Dispatched Orders not found')
          } else {
            setBarData([
              {
                time: '< 24 hours',
                orders: dispatchBarChartDetails?.freshOrdersCount ?? 0,
              },
              {
                time: '24–48 hours',
                orders: dispatchBarChartDetails?.recentOrdersCount ?? 0,
              },
              {
                time: '48–72 hours',
                orders: dispatchBarChartDetails?.staleOrdersCount ?? 0,
              },
              {
                time: '> 72 hours',
                orders: dispatchBarChartDetails?.agedOrdersCount ?? 0,
              },
            ])
            setShowBarChart(true)
            setBarChartError('')
            setIsLoading(false)
            setBarChartIsLoading(false)
          }
        },
        onError: ({ response: { data: errData } }: any) => {
          //notify(errData.message || 'Error...', 'error')
          setShowBarChart(false)
          setBarChartIsLoading(false)

          setBarChartError('Error loading bar chart data')
        },
      },
    )
  }

  useEffect(() => {
    getPartnerList()
    getPartnerOrderTypeList()
    //getAgeingOrderDetails()
    //getFulfillmentOrderDetails()
    /*if (
      filterVariables.partnerCodes.length === 1 &&
      filterVariables.partnerCodes[0] !== 'All' &&
      filterVariables.partnerCodes[0] !== ''
    ) {
      // getDispatchKPIDetails()
    }*/
    //getDispatchBarChartDetails()
  }, [])
  return (
    <div
      className={CN(
        'relative flex items-center justify-start bg-N-50 h-full w-full pt-4 px-4 sm:px-8 pb-4',
        className,
      )}>
      <div className='flex flex-col h-full w-full rounded-lg bg-N-100 shadow px-4 sm:px-12 py-6 sm:py-7 overflow-auto'>
        {/* Filters */}
        <div className='flex flex-col sm:flex-row sm:flex-wrap gap-4 items-start bg-white p-4 rounded-xl shadow mb-6 w-full'>
          {/* Customer */}
          <div className='flex flex-col sm:flex-row gap-4 sm:gap-4 items-start sm:items-center sm:flex-1'>
            <span className='text-N-500 text-se font-semibold whitespace-nowrap'>
              Customer
            </span>
            <div className='w-3/4 sm:min-w-[250px]'>
              <MultiSelect
                value={selectedPartners}
                onChange={(e: any) => {
                  setSelectedPartners(e)
                  setFilterVariables({
                    ...filterVariables,
                    partnerCodes: e.map((partner: Partner) => partner.value),
                  })
                  setselectedOrderTypes([
                    { label: 'All', value: '', disabled: false },
                  ])
                  setselectedFulfillmentOrderTypes([
                    { label: 'All', value: '', disabled: false },
                  ])
                }}
                options={partnerList}
                isMulti={true}
                placeholder='Select Customers'
              />
            </div>
          </div>

          {/* Order Type */}
          <div className='flex flex-col sm:flex-row gap-2 sm:gap-4 items-start sm:items-center sm:flex-1'>
            <span className='text-N-500 text-se font-semibold whitespace-nowrap'>
              Order Type
            </span>
            <div className='w-3/4 sm:min-w-[250px]'>
              <MultiSelect
                value={selectedOrderTypes}
                onChange={(e: any) => {
                  setselectedOrderTypes(e)
                  setFilterVariables({
                    ...filterVariables,
                    orderTypes: e.map((orderType: any) => orderType.label),
                  })
                }}
                options={
                  selectedPartners.length > 1 ||
                  selectedPartners.some((p: any) => p.label === 'All')
                    ? partnerOrderTypeList.filter((p: any) => p.label === 'All')
                    : partnerOrderTypeList
                }
                isMulti={true}
                placeholder='Select Order Type'
              />
            </div>
          </div>

          {/* Buttons */}
          <div className='flex gap-3 justify-end w-full sm:w-auto mt-4 sm:mt-0'>
            <button
              className={`${
                selectedPartners.length === 0 ||
                selectedFulfillmentOrderTypes.length === 0
                  ? 'bg-P-200 cursor-not-allowed'
                  : 'bg-P-500 hover:bg-P-600'
              } text-white px-6 py-2 rounded text-sm`}
              onClick={handleApplyClick}
              disabled={
                selectedPartners.length === 0 || selectedOrderTypes.length === 0
              }>
              Apply
            </button>
            <button
              className={`${
                selectedPartners.length === 0 ||
                selectedFulfillmentOrderTypes.length === 0
                  ? 'bg-P-200 cursor-not-allowed'
                  : 'bg-P-500 hover:bg-P-600'
              } text-white px-6 py-2 rounded text-sm`}
              onClick={handleRefreshClick}
              disabled={
                selectedPartners.length === 0 || selectedOrderTypes.length === 0
              }>
              <FiRefreshCw size={18} />
            </button>
          </div>
        </div>

        {/* Ageing Section */}
        <section>
          <h2 className='text-lg font-semibold mb-3'>Ageing Analysis</h2>
          <div className='flex flex-col xl:flex-row gap-8'>
            <div className='w-full xl:w-1/2'>
              <OrderStatusCard
                openOrders={openOrders}
                statuses={orderStatusSummary}
                isLoading={isLoading}
                filterVariables={filterVariables}
              />
            </div>
            <div className='w-full xl:w-1/2'>
              <OrderAgeingCard
                ageRanges={ageRanges}
                isLoading={isLoading}
                filterVariables={filterVariables}
              />
            </div>
          </div>
        </section>

        {/* Fulfillment Filters */}
        <section>
          <h2 className='text-lg font-semibold mb-3 mt-10'>
            Order Fulfillment
          </h2>
          <div className='flex flex-col sm:flex-row sm:flex-wrap gap-4 items-start bg-white p-4 rounded-xl shadow mb-6 w-full'>
            {/* Period */}
            <div className='flex flex-col sm:flex-row gap-2 sm:gap-4 items-start sm:items-center sm:flex-1'>
              <span className='text-N-500 text-se font-semibold whitespace-nowrap'>
                Period
              </span>
              <div className='w-3/4 sm:min-w-[250px]'>
                <DateRangePickerComponent
                  format='yyyy/MM/dd'
                  delayUpdate={true}
                  placeholder='Select a range'
                  min={new Date('2025-01-01')}
                  onChange={(e: any) => {
                    const fromDate = new Date(e.target.startDate)
                    fromDate.setHours(0, 0, 0, 0) // Set to 00:00

                    const toDate = new Date(e.target.endDate)
                    toDate.setHours(23, 59, 59, 999) // Set to 23:59
                    const diffMs = toDate.getTime() - fromDate.getTime()
                    const maxDiffMs = 31 * 24 * 60 * 60 * 1000 // 31 days in ms

                    if (diffMs > maxDiffMs) {
                      // Show an error (customize to your app: toast, alert, error state, etc.)
                      setIsDateError(true)
                      setDateError('Date range cannot exceed 31 days.')
                      return
                    } else {
                      setIsDateError(false)
                      setDateError('')
                    }
                    setFilterVariables({
                      ...filterVariables,
                      fromDate,
                      toDate,
                    })
                  }}
                  startDate={filterVariables?.fromDate}
                  endDate={filterVariables?.toDate}
                />
                {/* Error Message */}
                {isDateError && (
                  <span className='text-R-600 text-sm mt-1 block'>
                    {dateError}
                  </span>
                )}
              </div>
            </div>

            {/* Fulfillment Order Type */}
            <div className='flex flex-col sm:flex-row gap-2 sm:gap-4 items-start sm:items-center sm:flex-1'>
              <span className='text-N-500 text-se font-semibold whitespace-nowrap'>
                Order Type
              </span>
              <div className='w-3/4 sm:min-w-[250px]'>
                <MultiSelect
                  value={selectedFulfillmentOrderTypes}
                  onChange={(e: any) => {
                    setselectedFulfillmentOrderTypes(e)
                    setFilterVariables({
                      ...filterVariables,
                      fulfillmentOrderTypes: e.map((o: any) => o.label),
                    })
                  }}
                  options={
                    selectedPartners.length > 1 ||
                    selectedPartners.some((p: any) => p.label === 'All')
                      ? partnerOrderTypeList.filter(
                          (p: any) => p.label === 'All',
                        )
                      : partnerOrderTypeList
                  }
                  isMulti={true}
                  placeholder='Select Order Type'
                />
              </div>
            </div>

            {/* Fulfillment Buttons */}
            <div className='flex gap-3 justify-end w-full sm:w-auto mt-4 sm:mt-0'>
              <button
                className={`${
                  selectedPartners.length === 0 ||
                  selectedFulfillmentOrderTypes.length === 0
                    ? 'bg-P-200 cursor-not-allowed'
                    : 'bg-P-500 hover:bg-P-600'
                } text-white px-6 py-2 rounded text-sm`}
                onClick={handleFulfillmentApplyClick}
                disabled={
                  selectedPartners.length === 0 ||
                  selectedFulfillmentOrderTypes.length === 0 ||
                  isDateError === true
                }>
                Apply
              </button>
              <button
                className={`${
                  selectedPartners.length === 0 ||
                  selectedFulfillmentOrderTypes.length === 0
                    ? 'bg-P-200 cursor-not-allowed'
                    : 'bg-P-500 hover:bg-P-600'
                } text-white px-6 py-2 rounded text-sm`}
                onClick={handleFulfillmentRefreshClick}
                disabled={
                  selectedPartners.length === 0 ||
                  selectedFulfillmentOrderTypes.length === 0
                }>
                <FiRefreshCw size={18} />
              </button>
            </div>
          </div>

          {/* Fulfillment Cards */}
          <div className='flex flex-col xl:flex-row gap-0 mb-0 rounded-xl'>
            <div className='bg-white w-full xl:w-1/2 rounded-l-xl  p-4 md:p-6'>
              <div className='flex items-center gap-x-5 z-[50] mb-4'>
                <OrderTotalCard
                  stats={fulfillmentOrderSummary}
                  totalOrders={fulfillmentOrderCount}
                  isTotalCardLoading={isTotalCardLoading}
                />
              </div>

              <div className='flex items-center gap-x-5 z-[50] mt-20'>
                <DispatchPieChart
                  pieData={pieData}
                  showPieChart={showPieChart}
                  pieError={pieError}
                  pieChartIsLoading={pieChartIsLoading}
                />
              </div>
            </div>
            <div className='bg-white w-full xl:w-1/2  p-4 md:p-6 rounded-r-xl'>
              <DispatchBarChart
                barData={barData}
                showBarChart={showBarChart}
                barChartError={barChartError}
                barChartIsLoading={barChartIsLoading}
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

export default MWDashboard
